import {
  FACT_INSTANCE_SEP,
  factInstanceKey,
  splitFactKey,
  type EntityState,
  type FactState,
} from './entities-fold.js';
import {
  factCreate,
  factEdit,
  validityBound,
  type FactCreatePayload,
  type FactEditPayload,
  type FactMetaInput,
  type FactValidity,
  type ValidityBoundSide,
} from './fact-ops.js';
import type { Precision } from './predicate.js';

// crypto.randomUUID is available at runtime in browsers, Web Workers,
// Cloudflare Workers, and Node 19+ — mirror entity.ts rather than pulling
// DOM lib types into every consumer for one call.
declare const crypto: { randomUUID(): string };

/**
 * Valid-time segments (ADR-0005). A `(subject, predicate)` no longer holds
 * *a value* — it holds **`value(t)`**, a function of in-world time chosen by
 * which **segment** covers day `t`. `instance ≡ segment`: each segment is an
 * ordinary transaction-time op chain stored under a `predicate#seg_…` key
 * (the existing fact-instance slot, generalized — ADR-0005 §1).
 *
 * The **bare predicate** (no `#…` suffix) is the *implicit first segment*:
 * a predicate that was only ever asserted once renders exactly as today
 * (progressive disclosure — ADR-0005 Consequences/UX). Additional segments
 * carry an explicit minted `seg_…` id.
 *
 * Tracer scope (S1, #188): proven on `ruled_by` (uniqueness key `∅`). The
 * machinery is predicate-agnostic, but the editor fork + timeline strip are
 * only wired for the singular-segment case; `{object}` / `{role}` keys are S2.
 *
 * Day arithmetic note (ADR-0006): the *branded* `CanonicalDay` axis +
 * `[from, to)` chokepoint live in `@dl/calendar` (`instant.ts`). `@dl/shared`
 * is a dependency OF `@dl/calendar` (not the reverse), so segments cannot
 * import it without a cycle. The membership below operates on the plain
 * integer `FactValidity` bounds the op log already stores; it mirrors
 * `instant.ts`'s half-open semantics exactly (`from <= t < toExclusive`,
 * with the stored *inclusive* `to` mapped to `to + 1`).
 */

/** Kind prefix for a minted segment id (ADR-0004 mint discipline; `seg_…`). */
export const SEGMENT_ID_PREFIX = 'seg_';

/**
 * Mint an opaque, immutable segment id. Charset is `seg_` + a UUID v4 — the
 * UUID alphabet excludes the reserved separators `#` and `:`, so the
 * composed `predicate#seg_…` key stays unambiguous against repeatable-fact
 * instances (`#op_…`) and the `__pack` / `__campaign` reserved slots. Beliefs
 * are themselves `seg_…` segments now (owner-tagged — ADR-0005 §8).
 */
export function mintSegmentId(): string {
  return `${SEGMENT_ID_PREFIX}${crypto.randomUUID()}`;
}

/** True when `instance` was minted by {@link mintSegmentId}. */
export function isSegmentId(instance: string | null | undefined): boolean {
  return typeof instance === 'string' && instance.startsWith(SEGMENT_ID_PREFIX);
}

/**
 * Compose the op-log predicate key for an explicit segment on `predicate`.
 * Reuses {@link factInstanceKey} so the segment folds through the same
 * `(subject, predicate#instance)` chain machinery as repeatable instances.
 */
export function segmentKey(predicate: string, segmentId: string): string {
  return factInstanceKey(predicate, segmentId);
}

/**
 * Split a fact-map / op-log key into `{ base, segment }` where `segment` is
 * the explicit `seg_…` id, or `null` for the bare-predicate implicit first
 * segment. A belief is itself a `seg_…` segment (owner-tagged — ADR-0005 §8),
 * so it splits like any other segment; only a repeatable-instance (`#op_…`) or
 * `__*` key returns `segment: null` — callers gate on the `seg_` prefix via
 * {@link isSegmentId}.
 */
export function splitSegmentKey(key: string): { base: string; segment: string | null } {
  const { predicate, instance } = splitFactKey(key);
  if (instance !== null && isSegmentId(instance)) return { base: predicate, segment: instance };
  // Not a segment instance (repeatable `#op_…` / plain): treat the whole key
  // as the base with no explicit segment.
  return { base: key, segment: null };
}

/**
 * Does day `t` fall inside `validity`'s half-open `[from, to)` window?
 * (ADR-0005 §4). Storage is half-open: `from` is INCLUSIVE, `to` is
 * EXCLUSIVE. A successor opening at `from = at_day` and a predecessor closed
 * at `to = at_day` therefore SHARE the boundary number with zero overlap —
 * the successor owns day `at_day`, the predecessor does not. The exclusive
 * `to` is converted back to an inclusive "through day `to − 1`" only at the
 * display edge (segment-strip / instant.ts `toInclusiveEnd`), never in query
 * logic. Absent bounds are open (timeless in that direction).
 */
export function validityCoversDay(validity: FactValidity | null, t: number): boolean {
  if (validity === null) return true; // timeless
  if (validity.from !== undefined && t < validity.from) return false;
  if (validity.to !== undefined && t >= validity.to) return false; // exclusive upper
  return true;
}

/**
 * The reserved `{object}` uniqueness-key token (ADR-0005 §2). Re-exported via
 * `predicate.ts`; duplicated here as a value so `segments.ts` (which is a
 * dependency-free leaf besides `fact-ops`) need not import the heavier
 * `predicate.ts` module just for one string constant.
 */
const OBJECT_TOKEN = 'object';

/**
 * Compute the **uniqueness-group key** for a fact value under a predicate's
 * declared `uniqueness_key` (ADR-0005 §2). This is the discriminator that
 * sub-divides one `(subject, base-predicate)` into the groups *within which*
 * two segments may not overlap in valid time:
 *
 *  - `undefined` / not segmented → `null` (caller treats the predicate as
 *    non-segmented; no grouping).
 *  - `[]` (key `∅`)            → `''` — every segment of the `(subject, base)`
 *    competes in ONE group ("at most one ruler at a time").
 *  - `['object']` (key `{object}`) → a stable JSON encoding of the fact's own
 *    value — each distinct value is its own group (`alias = Bob` vs
 *    `alias = Carol` coexist; two `alias = Bob` segments compete).
 *  - `[fld_…, …]` (tuple-field key) → a stable JSON encoding of the named
 *    tuple fields' values (one `government` holder per `role`; roles coexist).
 *    A non-record value, or a field absent from the record, contributes a
 *    `null` placeholder so malformed tuples still group deterministically.
 *
 * The encoding is order-independent over the declared key fields (the key
 * array order does not change the grouping) and stable across runs (so the
 * op-log replay gate is bit-deterministic).
 */
export function uniquenessGroupValue(
  uniquenessKey: readonly string[] | undefined,
  value: unknown,
): string | null {
  if (uniquenessKey === undefined) return null;
  if (uniquenessKey.length === 0) return ''; // key ∅ — single group
  if (uniquenessKey.length === 1 && uniquenessKey[0] === OBJECT_TOKEN) {
    // {object}: group by the fact's own value.
    return stableStringify(value);
  }
  // Tuple-field key: group by the named fields' values (sorted by field id so
  // the key-array order never changes the grouping).
  const rec =
    typeof value === 'object' && value !== null && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : null;
  const parts: [string, unknown][] = [...uniquenessKey]
    .sort()
    .map((fld) => [fld, rec ? (rec[fld] ?? null) : null]);
  return stableStringify(Object.fromEntries(parts));
}

/**
 * Deterministic JSON for grouping keys: objects are emitted with their keys
 * sorted so two equal records always stringify identically regardless of
 * insertion order. Primitives/arrays pass straight through `JSON.stringify`
 * (arrays keep their order — order is meaningful for a list value).
 */
function stableStringify(value: unknown): string {
  return JSON.stringify(value, (_k, v) => {
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      const out: Record<string, unknown> = {};
      for (const key of Object.keys(v as Record<string, unknown>).sort()) {
        out[key] = (v as Record<string, unknown>)[key];
      }
      return out;
    }
    return v;
  });
}

/**
 * A resolved valid-time segment: the chain key it folded from, its current
 * value + validity, plus the (subject, base-predicate) it belongs to.
 */
export interface ResolvedSegment {
  /** The op-log key this segment folded from (`predicate` or `predicate#seg_…`). */
  key: string;
  /** The explicit segment id, or `null` for the bare-predicate first segment. */
  segment: string | null;
  value: unknown;
  validity: FactValidity | null;
}

/**
 * Select the segment whose half-open validity covers in-world day `t` from a
 * set of segments sharing one `(subject, base-predicate)` and uniqueness key
 * `∅`. Resolution rules (ADR-0005 §4/§5):
 *
 *  - Half-open membership: the shared boundary day belongs to the SUCCESSOR.
 *  - `t === null` (timeless / no cursor): the segment with the latest start
 *    (most-recent reign) wins — the "now" read with no filter.
 *  - Overlap is a consistency error, not a feature: when more than one
 *    segment covers `t`, the later-starting one is returned deterministically
 *    (the §5 error-recovery rule; the overlap advisory is raised elsewhere).
 *
 * Returns `null` when no segment covers `t`.
 */
export function resolveSegmentAt(
  segments: readonly ResolvedSegment[],
  t: number | null,
): ResolvedSegment | null {
  if (segments.length === 0) return null;
  if (segments.length === 1) {
    const only = segments[0]!;
    if (t === null) return only;
    return validityCoversDay(only.validity, t) ? only : null;
  }
  // Order by start day ascending (timeless `from` = −∞ sorts first), so the
  // last match is the latest-starting covering segment.
  const ordered = [...segments].sort((a, b) => startOf(a) - startOf(b));
  if (t === null) {
    // No filter: the most-recent (latest-starting) segment is "now".
    return ordered[ordered.length - 1]!;
  }
  let chosen: ResolvedSegment | null = null;
  for (const seg of ordered) {
    if (validityCoversDay(seg.validity, t)) chosen = seg; // later start wins
  }
  return chosen;
}

function startOf(seg: ResolvedSegment): number {
  return seg.validity?.from ?? Number.NEGATIVE_INFINITY;
}

/**
 * The two ops a {@link factSucceed} expands into. `fact.succeed` is **sugar
 * over two ordinary ops** (ADR-0005 §3) — the substrate has no cross-chain
 * transaction, so the caller submits both independently:
 *
 *  - `close` — a `fact.edit` on the *predecessor* segment chain that stamps
 *    its exclusive `to = at_day` (half-open; the predecessor stays valid
 *    through the day before the change). This is the destructive half; the UI surfaces it
 *    as a **confirmable** action (no silent merge — ADR-0005 §3).
 *  - `open` — a `fact.create` on a NEW `predicate#seg_…` chain with the new
 *    value and `from = at_day`.
 */
export interface SuccessionOps {
  /** Edit on the predecessor chain that closes it (half-open exclusive `to = at_day`). */
  close: { key: string; payload: FactEditPayload };
  /** Create on the new segment chain that opens the successor (`from = at_day`). */
  open: { key: string; segmentId: string; payload: FactCreatePayload };
}

export interface FactSucceedInput {
  /** The base predicate (without any `#segment` suffix). */
  predicate: string;
  /** The op-log key of the predecessor segment chain being closed. */
  predecessorKey: string;
  /** The current value of the predecessor (carried into the close edit). */
  predecessorValue: unknown;
  /** The predecessor's current validity (its `from` is preserved). */
  predecessorValidity: FactValidity | null;
  /** In-world day the change takes effect; the successor owns this day. */
  atDay: number;
  /** The successor's new value. */
  value: unknown;
  /** Optional meta carried onto the successor create (status/owner/reveal). */
  successorMeta?: Omit<FactMetaInput, 'validity'>;
  /** Segment id for the successor; defaults to a freshly minted `seg_…`. */
  segmentId?: string;
}

/**
 * Build the two ops a "this changed as of day X" succession expands into
 * (ADR-0005 §3). Half-open boundary: predecessor closes at exclusive
 * `to = atDay`, successor opens at `from = atDay`, so they share the boundary
 * number and day `atDay` belongs to the successor with zero overlap.
 *
 * The predecessor close PRESERVES the predecessor's `from` (it does not
 * become timeless); only its `to` is stamped.
 */
export function factSucceed(input: FactSucceedInput): SuccessionOps {
  const segmentId = input.segmentId ?? mintSegmentId();
  const closeValidity: FactValidity = {
    ...(input.predecessorValidity?.from !== undefined
      ? { from: input.predecessorValidity.from }
      : {}),
    // Preserve the predecessor's start-precision hint (ADR-0005 §5) — closing a
    // segment only stamps its exact `to`; how its START reads is unchanged.
    ...(input.predecessorValidity?.from !== undefined &&
    input.predecessorValidity.from_precision !== undefined
      ? { from_precision: input.predecessorValidity.from_precision }
      : {}),
    to: input.atDay,
  };
  const openValidity: FactValidity = { from: input.atDay };
  return {
    close: {
      key: input.predecessorKey,
      payload: factEdit(input.predecessorValue, { validity: closeValidity }),
    },
    open: {
      key: segmentKey(input.predicate, segmentId),
      segmentId,
      payload: factCreate(input.value, { ...input.successorMeta, validity: openValidity }),
    },
  };
}

/**
 * Precision rank for the in-world `Precision` enum, COARSE → FINE. A larger
 * number is a finer (more precise) bound. Used to compare two bounds' stated
 * precision and pick the coarser ("widest uncertainty") of the pair. `day` is
 * the implicit precision of a bound with no hint (the stored value is always an
 * exact day — ADR-0005 §5), so an absent hint ranks at `day`.
 */
const PRECISION_RANK: Record<Precision, number> = {
  year: 0,
  season: 1,
  month: 2,
  day: 3,
  hour: 4,
  minute: 5,
  second: 6,
};

/** The implicit precision of an exact day with no stated hint. */
const DEFAULT_PRECISION_RANK = PRECISION_RANK.day;

function precisionRank(p: Precision | undefined): number {
  return p === undefined ? DEFAULT_PRECISION_RANK : PRECISION_RANK[p];
}

/**
 * Does a validity bound carry a FUZZY (coarser-than-day) precision hint? A
 * bound the GM marked "year"/"season"/"month" is an honest fuzzy date: the
 * stored day anchors the START of that named period, but the GM has declared the
 * true value lies somewhere within it (ADR-0005 §5). `day` and finer are exact
 * for overlap purposes. An absent hint (no precision) is exact.
 */
export function isFuzzyBound(bound: { precision?: Precision } | null): boolean {
  if (bound === null) return false;
  return precisionRank(bound.precision) < DEFAULT_PRECISION_RANK;
}

/**
 * Do two overlapping segments overlap ONLY within a shared precision band —
 * i.e. is the overlap plausibly an artifact of a fuzzy bound rather than a real
 * two-values-on-one-day conflict (ADR-0005 §5)?
 *
 * This NEVER changes membership (resolution at `t` stays binary; the stored
 * days are exact). It is consumed only by the overlap auditor to MODULATE
 * severity: a deliberate fuzzy bound (the GM said "the 1060s", not "1063")
 * soft-warns instead of hard-warning, because the apparent collision lives
 * inside the uncertainty the GM explicitly declared.
 *
 * Rule: if EITHER segment carries a coarser-than-`day` precision on ANY of its
 * bounds, the overlap is within that declared precision band and the audit
 * softens. Both-exact (or finer) overlaps stay hard — an exact
 * two-rulers-on-one-day assertion is a genuine mistake.
 */
export function overlapWithinPrecisionBand(
  a: FactValidity | null,
  b: FactValidity | null,
): boolean {
  if (a === null || b === null) return false; // a timeless segment is never fuzzy-bounded
  const sides: ValidityBoundSide[] = ['from', 'to'];
  for (const range of [a, b]) {
    for (const side of sides) {
      if (isFuzzyBound(validityBound(range, side))) return true;
    }
  }
  return false;
}

/** Re-export the separator so segment consumers don't reach into entities-fold. */
export { FACT_INSTANCE_SEP };

/**
 * Collect the valid-time segments of a `(subject, base-predicate)` from a
 * folded {@link EntityState}, as {@link ResolvedSegment}s ready for
 * {@link resolveSegmentAt}. Includes the bare-slot first segment (`base`) and
 * every explicit `<base>#seg_…` successor (ADR-0005 §1). Soft-deleted and
 * null-valued segments are dropped; belief / repeatable (`#op_…`) keys are not
 * valid-time segments and are excluded.
 *
 * This is GM-side, omniscient display logic over the GM's OWN folded replica —
 * NOT a non-GM read. It constructs no `VisibleFact`, so the phantom-Viewer
 * chokepoint is untouched; perspective reads still flow through
 * `packages/projection`.
 */
export function baseSegmentsOf(
  facts: Readonly<Record<string, FactState>>,
  base: string,
): ResolvedSegment[] {
  const out: ResolvedSegment[] = [];
  for (const [key, fact] of Object.entries(facts)) {
    if (fact.soft_deleted) continue;
    const split = splitSegmentKey(key);
    const isBareSlot = key === base;
    const isSegSuccessor =
      split.base === base && split.segment !== null && isSegmentId(split.segment);
    if (!isBareSlot && !isSegSuccessor) continue;
    const value = fact.versions[fact.versions.length - 1]?.value;
    if (value === undefined || value === null) continue;
    out.push({
      key,
      segment: isSegSuccessor ? split.segment : null,
      value,
      validity: fact.validity ?? null,
    });
  }
  return out;
}

// ── ADR-0005 §9 — system-derived succession at ratification ────────

/**
 * One existing segment a draft might succeed. A minimal projection of a
 * folded {@link FactState} carrying only what the succession derivation needs:
 * the chain key, current value, validity, and owner. Built GM-side over the
 * GM's own folded replica (no `VisibleFact` — phantom-Viewer chokepoint
 * untouched).
 */
export interface ExistingSegment {
  /** The op-log key this segment folds from (`predicate` or `predicate#seg_…`). */
  key: string;
  value: unknown;
  validity: FactValidity | null;
  /**
   * Owner key for owner-scoping (ADR-0005 §8): canon vs a belief Viewer. A
   * draft canon segment may only imply closing a canon predecessor; a belief
   * draft only its own owner's predecessors. Pass a stable string (e.g.
   * `'canon'` or the belief viewer key).
   */
  ownerKey: string;
}

/**
 * The draft whose validity might imply a succession. Mirrors the fields the
 * inbox already holds for a `status:'draft'` fact.
 */
export interface DraftSegment {
  /** The draft's own chain key (excluded from predecessor candidates). */
  key: string;
  value: unknown;
  validity: FactValidity | null;
  ownerKey: string;
}

/**
 * A single system-derived predecessor-close proposal (ADR-0005 §9). The LLM
 * never authors this — it is *computed* from a draft's validity and surfaced
 * as a **confirmable** line in the same ratification card. The GM ratifies,
 * edits {@link ImpliedClose.atDay}, or rejects; nothing touches canon until
 * confirmation.
 */
export interface ImpliedClose {
  /** Op-log key of the predecessor segment chain to close. */
  predecessorKey: string;
  /** The predecessor's current value (carried into the close `fact.edit`). */
  predecessorValue: unknown;
  /** The predecessor's current validity (its `from` / precision are preserved). */
  predecessorValidity: FactValidity | null;
  /**
   * The half-open exclusive `to` the predecessor would be closed at — equal to
   * the draft's `from` (the successor owns that day; the predecessor does not).
   * The GM may edit this boundary before confirming.
   */
  atDay: number;
  /**
   * True when the predecessor is currently open-ended (`to` absent) — the
   * common "Aria still reigns" case where promoting the draft *ends* her
   * reign. False when the predecessor already had a `to` that the draft now
   * overlaps (a genuine pre-existing overlap the close would also resolve).
   */
  predecessorWasOpen: boolean;
}

/**
 * Derive the predecessor-close(s) implied by promoting a dated draft
 * assertion to canon (ADR-0005 §9). Pure: given the draft segment and the
 * existing segments of the SAME `(subject, base-predicate)`, return one
 * {@link ImpliedClose} per existing segment that **precedes or overlaps** the
 * draft within the draft's uniqueness group and owner.
 *
 * This is the structural heart of "the LLM never writes canon directly": the
 * model only ever emits the additive dated assertion; the *close* is computed
 * here and confirmed by the GM. No silent merge — the result is a proposal,
 * not an applied op.
 *
 * Rules:
 *  - Only segmented predicates participate (`uniquenessKey !== undefined`);
 *    otherwise the empty array is returned (a flat predicate just overwrites).
 *  - The draft must carry a concrete `from` (the succession boundary). A
 *    timeless draft implies no close — nothing to succeed *at*.
 *  - Candidates are **owner-scoped** (§8): only existing segments whose
 *    `ownerKey` matches the draft's compete. Canon and a contradicting belief
 *    legitimately coexist; one never closes the other.
 *  - Candidates must share the draft's **uniqueness group** (§2): key ∅ →
 *    every segment competes; `{object}` / tuple-field keys → only the matching
 *    group ("Aria succeeds Caedric as *monarch*" leaves the chancellor alone).
 *  - A candidate precedes/overlaps the draft iff it has no `to`, or its `to`
 *    is strictly after the draft's `from` (half-open: a `to === from`
 *    predecessor already ends exactly where the draft begins — no overlap, no
 *    close). Candidates entirely after the draft are not predecessors.
 *  - The draft's own chain key is never its own predecessor.
 *
 * The returned closes are ordered by predecessor start day so the UI lists
 * them deterministically (op-log replay stays bit-stable).
 */
export function deriveImpliedSuccession(
  draft: DraftSegment,
  existing: readonly ExistingSegment[],
  uniquenessKey: readonly string[] | undefined,
): ImpliedClose[] {
  // A flat (non-segmented) predicate has no valid-time succession — a second
  // assertion overwrites the head; nothing to close.
  if (uniquenessKey === undefined) return [];
  // No concrete succession boundary → no implied close.
  const atDay = draft.validity?.from;
  if (atDay === undefined) return [];

  const draftGroup = uniquenessGroupValue(uniquenessKey, draft.value);

  const closes: ImpliedClose[] = [];
  for (const seg of existing) {
    if (seg.key === draft.key) continue; // never succeed itself
    if (seg.ownerKey !== draft.ownerKey) continue; // owner-scoped (§8)
    // Same uniqueness group (§2): key ∅ → all compete; {object}/tuple → match.
    if (uniquenessGroupValue(uniquenessKey, seg.value) !== draftGroup) continue;
    const segTo = seg.validity?.to;
    // Half-open: a predecessor that already ends at-or-before the draft's
    // `from` does NOT overlap (it ends exactly where the draft begins, or
    // earlier) — no close implied.
    if (segTo !== undefined && segTo <= atDay) continue;
    // A candidate whose own `from` is at-or-after the draft's `from` is a
    // *successor*, not a predecessor — promoting the draft does not end it.
    const segFrom = seg.validity?.from;
    if (segFrom !== undefined && segFrom >= atDay) continue;
    closes.push({
      predecessorKey: seg.key,
      predecessorValue: seg.value,
      predecessorValidity: seg.validity,
      atDay,
      predecessorWasOpen: segTo === undefined,
    });
  }
  return closes.sort(
    (a, b) =>
      (a.predecessorValidity?.from ?? Number.NEGATIVE_INFINITY) -
      (b.predecessorValidity?.from ?? Number.NEGATIVE_INFINITY),
  );
}

/**
 * Build the `fact.edit` payload that closes a predecessor at `atDay` (ADR-0005
 * §9, the confirmed half of {@link deriveImpliedSuccession}). Preserves the
 * predecessor's `from` + `from_precision`; stamps the half-open exclusive
 * `to = atDay`. Mirrors {@link factSucceed}'s close half so the two paths
 * produce identical ops. The GM may pass an edited `atDay` (boundary edit).
 */
export function closeImpliedPredecessor(close: ImpliedClose, atDay: number): {
  key: string;
  payload: FactEditPayload;
} {
  const v = close.predecessorValidity;
  const closeValidity: FactValidity = {
    ...(v?.from !== undefined ? { from: v.from } : {}),
    ...(v?.from !== undefined && v.from_precision !== undefined
      ? { from_precision: v.from_precision }
      : {}),
    to: atDay,
  };
  return {
    key: close.predecessorKey,
    payload: factEdit(close.predecessorValue, { validity: closeValidity }),
  };
}

/**
 * Resolve the value of a `(subject, base-predicate)` AS OF in-world day `t`
 * (ADR-0005 §1 — `value(t): InWorldTime → V`). Folds the base's segments
 * (bare slot + `seg_…` successors) and selects the one whose half-open
 * validity covers `t` via {@link resolveSegmentAt}. `t === null` is the
 * "now / timeless" read (latest-starting segment wins).
 *
 * Returns the covering {@link ResolvedSegment}, or `null` when no segment
 * covers `t` (e.g. browsing to a day before any segment began).
 *
 * GM-side display helper (see {@link baseSegmentsOf}); the phantom-Viewer
 * chokepoint stays in `packages/projection`.
 */
export function resolveBaseSegmentAt(
  facts: Readonly<Record<string, FactState>>,
  base: string,
  t: number | null,
): ResolvedSegment | null {
  return resolveSegmentAt(baseSegmentsOf(facts, base), t);
}

/**
 * Convenience over {@link resolveBaseSegmentAt} taking the whole
 * {@link EntityState}. Returns the value covering day `t`, or `undefined`
 * when no segment covers it (mirrors `currentFactValue`'s undefined-for-absent
 * contract so callers can swap one for the other).
 */
export function resolveEntityValueAt(
  entity: EntityState | undefined,
  base: string,
  t: number | null,
): unknown {
  if (!entity) return undefined;
  const seg = resolveBaseSegmentAt(entity.facts, base, t);
  return seg === null ? undefined : seg.value;
}
